import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import {
    AlertColor,
    Autocomplete,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    MenuItem,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    AdCreativeSource,
    AdSize,
    AdTagImportSource,
    AdTagPlacement,
    AdTagPlacementData,
    AdTagPlacementStatus,
    AdTagTemplate,
    AdTagType,
    Project,
} from '../../types/AdTag';
import { MediaType, MediaTypeName } from '../../types/MediaType';
import { FileInfo } from '../../types/S3';
import ApiService from '../../ApiService';
import CDButton from '../../components/CDButton';
import Row from '../../components/Row';
import { CDTextField } from '../../components/CDTextField';
import AppLoader from '../../components/AppLoader';
import CDAutocompleteEndless from '../../components/CDAutocompleteEndless';
import { Dealer } from '../../types/Dealer';
import { Campaign, CampaignAdGroup } from '../../types/Campaign';
import AccessGuard from '../../components/AccessGuard';
import { AGENCY_CAMPAIGNS, USER_GROUP_ADMIN } from '../../types/User';
import AgencySelector from '../../components/AgencySelector';
import { useUserContext } from '../../hooks/Contexts/UserContext';

const initialValues = {
    id: undefined as number | undefined,
    dealer: null as Dealer | null,
    campaign: null as Campaign | null,
    campaignAdGroup: null as CampaignAdGroup | null,
    tagType: 'VAST_VIDEO' as keyof typeof AdTagType,
    adTagTemplateId: 1, // "Static Image HTML" as default
    adSize: '160x600' as keyof typeof AdSize,
    dspPlatform: 'Amazon' as string,
    mediaType: MediaType.OLV as keyof typeof MediaType,
    adCreativeSource: 'FTP' as keyof typeof AdCreativeSource,
    adTagImportSourceId: null as number | null,
    filenameKey: null as string | null,
    filename: null as string | null,
    projectId: undefined as string | undefined,
    adUrl: undefined as string | undefined,
    adName: null as string | null,
    clickThroughUrl: undefined as string | undefined,
    impressionUrl: undefined as string | undefined,
    emailNotification: undefined as boolean | undefined,
    status: undefined as string | undefined,
    project: undefined as Project | undefined,
};

export type EditAdTagPlacementRef = {
    edit: (agencyId: number | null, adTagPlacement?: AdTagPlacement) => void;
};

type Props = {
    reloadGrid(): void;
    showInfoMessage: (alertColor: AlertColor, message: string, onClose?: () => void) => void;
};

const popperProps = {
    sx: { '& .MuiTooltip-tooltip': { backgroundColor: 'white', color: 'black', boxShadow: 1, fontSize: 14 } },
};

const EditAdTagPlacement = forwardRef<EditAdTagPlacementRef, Props>(({ reloadGrid, showInfoMessage }, ref) => {
    const { userContext } = useUserContext();
    const [dspPlatforms, setDspPlatforms] = useState<string[]>([]);
    const [campaigns, setCampaigns] = useState<Campaign[] | null>(null);
    const [adTagTemplates, setAdTagTemplates] = useState<AdTagTemplate[] | null>(null);
    const [mediaFiles, setMediaFiles] = useState<FileInfo[] | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [formData, setFormData] = useState(initialValues);
    const [selectedAgencyId, setSelectedAgencyId] = useState<number | null>(null);
    const [isImperativeUpdate, setIsImperativeUpdate] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [agencyVideoSourceFolder, setAgencyVideoSourceFolder] = useState<string | null>(null);
    const [agencyImageSourceFolder, setAgencyImageSourceFolder] = useState<string | null>(null);
    const [adTagImportSource, setAdTagImportSource] = useState<AdTagImportSource | null>(null);

    const fetchCampaigns = (dealerId: number, campaignId?: number) => {
        setIsLoading(true);
        ApiService.getCampaignsForDealer(dealerId)
            .then(({ data: campaigns }) => {
                setCampaigns(campaigns);
                if (campaignId) {
                    const cmp = campaigns.find((c) => c.id === campaignId);
                    if (cmp) setFormData((prev) => ({ ...prev, campaign: cmp }));
                }
            })
            .finally(() => setIsLoading(false));
    };

    const fetchMediaFiles = (adTagImportSourceId: number, tagType: keyof typeof AdTagType) => {
        ApiService.getAdTagIncomingS3Files(
            adTagImportSourceId,
            formData.dspPlatform,
            formData.mediaType,
            tagType === 'VAST_VIDEO' ? '.mp4' : '.jpg,.jpeg,.png'
        ).then(({ data }) => setMediaFiles(data));
    };

    const fetchAdTagTemplates = () => ApiService.getTemplates().then(({ data }) => setAdTagTemplates(data));

    useImperativeHandle(ref, () => ({
        edit(agencyId: number | null, atp?: AdTagPlacement) {
            setSelectedAgencyId(agencyId || userContext.agencyId);
            if (atp) {
                const project = {
                    id: atp.projectId || '',
                    output: atp.adName || '',
                } as Project;
                setIsImperativeUpdate(true);
                setSelectedAgencyId(atp.dealer?.agencyId || null);
                setAdTagImportSource(atp.adTagImportSource || null);
                setFormData((prev) => ({
                    ...prev,
                    id: atp.id,
                    dealer: atp.dealer,
                    campaignAdGroup: atp.campaignAdGroup,
                    tagType: atp.tagType,
                    adTagTemplateId: atp.adTagTemplateId || 1,
                    adSize: atp.adSize || '160x600',
                    dspPlatform: atp.dspPlatform,
                    mediaType: atp.mediaType,
                    adCreativeSource: atp.adCreativeSource,
                    adTagImportSourceId: atp.adTagImportSourceId,
                    filename: atp.filename,
                    filenameKey: atp.filenameKey,
                    adName: atp.adName,
                    impressionUrl: atp.impressionUrl || undefined,
                    emailNotification: atp.emailNotification || undefined,
                    clickThroughUrl: atp.clickThroughUrl || undefined,
                    project,
                    projectId: atp.projectId || undefined,
                    adUrl: atp.adUrl || undefined,
                    status: atp.status || undefined,
                }));

                // fetch campaigns for the dealer and set the campaign
                if (atp.dealer?.id) fetchCampaigns(atp.dealer.id, atp.campaign?.id);
                if (atp.adTagImportSourceId) fetchMediaFiles(atp.adTagImportSourceId, atp.tagType);
                if (!adTagTemplates && atp.tagType === 'HTML_IMAGE') fetchAdTagTemplates();
            }
            handleDialogToggle();
        },
    }));

    useEffect(() => {
        if (!isOpen) return;

        ApiService.getDspMacroPlatforms()
            .then((response) => setDspPlatforms(response.data))
            .catch(() => {});
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || !selectedAgencyId || formData.id) return;
        ApiService.getAgency(selectedAgencyId)
            .then((response) => {
                setAgencyVideoSourceFolder(response.data.sourceFolderPath ?? null);
                setAgencyImageSourceFolder(response.data.imageSourceFolderPath ?? null);
                const folderForTagType =
                    formData.tagType === 'HTML_IMAGE'
                        ? response.data.imageSourceFolderPath
                        : response.data.sourceFolderPath;
                if (folderForTagType) {
                    ApiService.getAdTagImportSourceByAgency(selectedAgencyId, formData.tagType).then(({ data }) => {
                        setFormData((prev) => ({ ...prev, adTagImportSourceId: data.id }));
                    });
                }
            })
            .catch(() => {
                setAgencyVideoSourceFolder(null);
                setAgencyImageSourceFolder(null);
            });
    }, [isOpen, selectedAgencyId]);

    useEffect(() => {
        if (formData.id || !formData.adTagImportSourceId || !formData.dspPlatform || !formData.mediaType) return;
        fetchMediaFiles(formData.adTagImportSourceId, formData.tagType);
    }, [formData.id, formData.adTagImportSourceId, formData.dspPlatform, formData.mediaType, formData.tagType]);

    useEffect(() => {
        if (isImperativeUpdate) {
            setIsImperativeUpdate(false);
            return;
        }
        const { dealer, dspPlatform, filenameKey } = formData;
        if (!dealer?.dealerName || !dspPlatform || !filenameKey) return;
        const adName = [dealer?.dealerName, dspPlatform, filenameKey, formData.mediaType].filter((v) => v).join(' - ');
        setFormData((prev) => ({ ...prev, adName }));
    }, [formData.dealer, formData.dspPlatform, formData.filenameKey, formData.mediaType]);

    useEffect(() => {
        if (formData.tagType === 'VAST_VIDEO') {
            if (![MediaType.STV, MediaType.OLV].includes(formData.mediaType as MediaType)) {
                setFormData((prev) => ({ ...prev, mediaType: MediaType.OLV }));
            }
        } else if (formData.tagType === 'HTML_IMAGE') {
            if (![MediaType.Display].includes(formData.mediaType as MediaType)) {
                setFormData((prev) => ({ ...prev, mediaType: MediaType.Display }));
            }
        }
    }, [formData.tagType]);

    useEffect(() => {
        if (formData.id || !formData?.dealer?.id || !formData.dspPlatform || !formData.mediaType) {
            return;
        }

        ApiService.getHatCreativePixelByCriteria(formData.dealer.id, {
            platform: formData.dspPlatform,
            mediaType: formData.mediaType,
            isCas: true,
        }).then((response) => {
            const { data: creativePixel } = response;
            setFormData((prev) => ({ ...prev, impressionUrl: creativePixel?.url || '' }));
        });
    }, [formData.dealer, formData.dspPlatform, formData.id, formData.mediaType]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = event.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const {
            id,
            dealer,
            campaign,
            campaignAdGroup,
            tagType,
            mediaType,
            adTagTemplateId,
            adSize,
            dspPlatform,
            projectId,
            adUrl,
            adName,
            clickThroughUrl,
            impressionUrl,
            adCreativeSource,
            adTagImportSourceId,
            emailNotification,
            filenameKey,
            filename,
            status,
        } = formData;

        const preparedData: AdTagPlacementData = {
            id,
            dealerId: dealer?.id || 0,
            campaignId: campaign?.id,
            campaignAdGroupId: campaignAdGroup?.id,
            dspPlatform,
            projectId,
            adUrl,
            adName,
            clickThroughUrl,
            impressionUrl,
            tagType,
            mediaType,
            adTagTemplateId,
            emailNotification,
            adSize,
            adCreativeSource,
            adTagImportSourceId,
            filenameKey,
            filename,
            status,
        };

        let { data: created } = await ApiService.createAdTagPlacement(preparedData);
        handleDialogClose();
        reloadGrid();
        if (created.id) {
            let { data: published } = await ApiService.publishAdTagPlacement(created.id);
            if (published.status === AdTagPlacementStatus.ERROR) {
                showInfoMessage('error', `Error processing ${published.adName}: ${published.processMessage}`);
            }
        }
    };

    const handleDealerChange = (dealer: Dealer | null) => {
        setCampaigns(null);
        const clickThroughUrl = dealer?.url || '';
        setFormData((prev) => ({ ...prev, dealer, clickThroughUrl, campaign: null, campaignAdGroup: null }));
        if (dealer?.id) fetchCampaigns(dealer.id);
    };

    const handleCampaignChange = (event: React.ChangeEvent<{}>, campaign: Campaign | null) => {
        setFormData((prev) => ({ ...prev, campaign, campaignAdGroup: null }));
    };

    const handleAdGroupChange = (event: React.ChangeEvent<{}>, value: any) => {
        setFormData((prev) => ({ ...prev, campaignAdGroup: value }));
    };

    const handleAdTagTypeChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const newTagType = event.target.value as keyof typeof AdTagType;
        setMediaFiles(null);
        setFormData((prev) => ({
            ...prev,
            tagType: newTagType,
            adTagImportSourceId: null,
            filenameKey: null,
            filename: null,
            project: undefined,
            adName: null,
            projectId: undefined,
            adUrl: undefined,
            status: undefined,
        }));
        if (!adTagTemplates && newTagType === 'HTML_IMAGE') fetchAdTagTemplates();

        // Re-fetch import source for the new tag type
        if (!formData.id && selectedAgencyId) {
            const folderForTagType = newTagType === 'HTML_IMAGE' ? agencyImageSourceFolder : agencyVideoSourceFolder;
            if (folderForTagType) {
                ApiService.getAdTagImportSourceByAgency(selectedAgencyId, newTagType).then(({ data }) => {
                    setFormData((prev) => ({ ...prev, adTagImportSourceId: data.id }));
                });
            }
        }
    };

    const handleAdTagTemplateChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const templateId: any = +event.target.value;
        setFormData((prev) => ({ ...prev, adTagTemplateId: templateId }));
    };

    const handleAdSizeChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData((prev) => ({ ...prev, adSize: event.target.value as keyof typeof AdSize }));
    };

    const handleDspPlatformChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const dspPlatform = event.target.value;
        setFormData((prev) => ({ ...prev, dspPlatform, filenameKey: null, filename: null, adName: null }));
        setMediaFiles(null);
    };

    const handleMediaTypeChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const mediaType = event.target.value as keyof typeof MediaType;
        setFormData((prev) => ({ ...prev, mediaType, filenameKey: null, filename: null, adName: null }));
        setMediaFiles(null);
    };

    const handleAdCreativeSourceChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData((prev) => ({ ...prev, adCreativeSource: event.target.value as keyof typeof AdCreativeSource }));
    };

    const handleVideoFileChange = (event: React.ChangeEvent<{}>, value: string | null) => {
        const filename: any = value || '';
        const filenameKey: any = filename.split('.')[0] || '';
        setFormData((prev) => ({ ...prev, filenameKey, filename }));
    };

    const clearFormData = () => {
        setFormData(initialValues);
        setCampaigns(null);
        setMediaFiles(null);
        setAgencyVideoSourceFolder(null);
        setAgencyImageSourceFolder(null);
        setAdTagImportSource(null);
    };

    const handleDialogToggle = () => {
        setIsOpen(!isOpen);
    };

    const handleDialogClose = () => {
        handleDialogToggle();
        clearFormData();
    };

    const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = event.target;

        setFormData((prev) => ({
            ...prev,
            [name]: checked,
        }));
    };

    return (
        <>
            <Dialog open={isOpen} maxWidth="md" fullWidth>
                <AppLoader loading={isLoading} />

                <DialogTitle>{formData.id ? 'Edit' : 'Setup'} Ad Tag</DialogTitle>

                {isOpen && (
                    <form onSubmit={handleSubmit}>
                        <DialogContent sx={{ padding: '10px' }}>
                            <AccessGuard accessGroup={USER_GROUP_ADMIN}>
                                <AgencySelector
                                    variant="outlined"
                                    label="Agency"
                                    sx={{ padding: '10px', width: '100%' }}
                                    agencySelected={(agencyId) => {
                                        setSelectedAgencyId(agencyId);
                                        setFormData((prev) => ({ ...prev, dealer: null }));
                                    }}
                                    agencyId={selectedAgencyId}
                                />
                            </AccessGuard>
                            <CDAutocompleteEndless
                                sx={{ padding: '10px' }}
                                required
                                label="Select Advertiser"
                                parentId={selectedAgencyId}
                                value={formData.dealer}
                                onChange={handleDealerChange}
                                handleFetch={async (page, name, parentId) =>
                                    ApiService.getAllDealers(page, name, parentId)
                                }
                                getOptionLabel={(option: Dealer) => option.dealerName}
                            />

                            <Row sx={{ padding: '10px' }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Select Creative Source"
                                    select
                                    value={formData.adCreativeSource}
                                    onChange={handleAdCreativeSourceChange}
                                    slotProps={{
                                        select: { native: false },
                                    }}
                                >
                                    {Object.keys(AdCreativeSource).map((key) => (
                                        <MenuItem key={key} value={key}>
                                            {AdCreativeSource[key as keyof typeof AdCreativeSource]}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Row>

                            <Row sx={{ padding: '10px' }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Select Tag Type"
                                    select
                                    value={formData.tagType}
                                    onChange={handleAdTagTypeChange}
                                    slotProps={{
                                        select: { native: false },
                                    }}
                                >
                                    {Object.keys(AdTagType).map((key) => (
                                        <MenuItem key={key} value={key}>
                                            {AdTagType[key as keyof typeof AdTagType]}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Row>

                            {adTagTemplates &&
                                formData.tagType === 'HTML_IMAGE' &&
                                formData.adCreativeSource === 'FTP' && (
                                    <>
                                        <Row sx={{ padding: '10px' }}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Select Tag Template"
                                                select
                                                value={formData.adTagTemplateId}
                                                onChange={handleAdTagTemplateChange}
                                                slotProps={{
                                                    select: { native: false },
                                                }}
                                            >
                                                {adTagTemplates.map((t) => (
                                                    <MenuItem key={t.id} value={t.id}>
                                                        {t.name}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Row>
                                        <Row sx={{ padding: '10px' }}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Select Ad Size"
                                                select
                                                value={formData.adSize}
                                                onChange={handleAdSizeChange}
                                                slotProps={{
                                                    select: { native: false },
                                                }}
                                            >
                                                {Object.keys(AdSize).map((key) => (
                                                    <MenuItem key={key} value={key}>
                                                        {AdSize[key as keyof typeof AdSize]}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Row>
                                    </>
                                )}

                            {
                                <Row sx={{ padding: '10px' }}>
                                    <TextField
                                        fullWidth
                                        required
                                        size="small"
                                        label="Select Media Type"
                                        select
                                        value={formData.mediaType}
                                        onChange={handleMediaTypeChange}
                                        SelectProps={{ native: false }}
                                    >
                                        {(() => {
                                            const tagType = AdTagType[formData.tagType as keyof typeof AdTagType];
                                            const mediaTypes =
                                                tagType === AdTagType.VAST_VIDEO
                                                    ? [MediaType.OLV, MediaType.STV]
                                                    : tagType === AdTagType.HTML_IMAGE
                                                    ? [MediaType.Display]
                                                    : [];

                                            return mediaTypes.map((key) => (
                                                <MenuItem key={key} value={key}>
                                                    {MediaTypeName[key]}
                                                </MenuItem>
                                            ));
                                        })()}
                                    </TextField>
                                </Row>
                            }

                            <Row sx={{ padding: '10px' }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Select DSP Platform"
                                    select
                                    value={formData.dspPlatform || ''}
                                    onChange={handleDspPlatformChange}
                                    slotProps={{
                                        select: { native: false },
                                    }}
                                >
                                    {dspPlatforms.map((key) => (
                                        <MenuItem key={key} value={key}>
                                            {key}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Row>

                            {formData.adCreativeSource === 'FTP' && (
                                <>
                                    <Row sx={{ padding: '10px' }}>
                                        {(() => {
                                            const sourceFolderLabel =
                                                formData.tagType === 'HTML_IMAGE'
                                                    ? 'Image Source Folder'
                                                    : 'Video Source Folder';
                                            const activeSourceFolder =
                                                formData.tagType === 'HTML_IMAGE'
                                                    ? agencyImageSourceFolder
                                                    : agencyVideoSourceFolder;

                                            if (formData.id) {
                                                return (
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        label={sourceFolderLabel}
                                                        disabled
                                                        value={
                                                            adTagImportSource
                                                                ? `${adTagImportSource.s3Bucket}/${adTagImportSource.s3IncomingPath}`
                                                                : ''
                                                        }
                                                        slotProps={{ inputLabel: { shrink: true } }}
                                                        helperText="Managed by your agency settings"
                                                    />
                                                );
                                            }
                                            if (activeSourceFolder) {
                                                return (
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        label={sourceFolderLabel}
                                                        disabled
                                                        value={activeSourceFolder}
                                                        slotProps={{ inputLabel: { shrink: true } }}
                                                        helperText="Managed by your agency settings"
                                                    />
                                                );
                                            }
                                            return (
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label={sourceFolderLabel}
                                                    disabled
                                                    error
                                                    value=""
                                                    slotProps={{ inputLabel: { shrink: true } }}
                                                    helperText="Ad Serving not enabled for this Agency. Contact the Account Manager to enable it."
                                                />
                                            );
                                        })()}
                                    </Row>
                                    {mediaFiles && (
                                        <Tooltip
                                            title="Files that are already assigned to a tag with the same DSP Platform and Media Type will not appear in this list."
                                            placement="left"
                                            slotProps={{
                                                popper: popperProps,
                                            }}
                                        >
                                            <Autocomplete
                                                sx={{ padding: '10px' }}
                                                options={mediaFiles.map((file) => file.name)}
                                                value={formData.filename || ''}
                                                onChange={handleVideoFileChange}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        fullWidth
                                                        required
                                                        size="small"
                                                        label="Select File"
                                                        name="filename"
                                                    />
                                                )}
                                            />
                                        </Tooltip>
                                    )}

                                    <Row sx={{ padding: '10px' }}>
                                        <CDTextField
                                            fullWidth
                                            required
                                            name="filenameKey"
                                            label="Filename Key"
                                            variant="outlined"
                                            value={formData.filenameKey || ''}
                                            onChange={handleChange}
                                        />
                                    </Row>

                                    <Row sx={{ padding: '10px' }}>
                                        <CDTextField
                                            fullWidth
                                            required
                                            name="adName"
                                            label="Ad Tag Name"
                                            variant="outlined"
                                            value={formData.adName || ''}
                                            onChange={handleChange}
                                        />
                                    </Row>
                                </>
                            )}
                            <Row sx={{ padding: '10px' }}>
                                <CDTextField
                                    fullWidth
                                    name="clickThroughUrl"
                                    label="Click-Through URL"
                                    variant="outlined"
                                    value={formData.clickThroughUrl || ''}
                                    onChange={handleChange}
                                />
                            </Row>
                            <Row sx={{ padding: '10px' }}>
                                <CDTextField
                                    fullWidth
                                    name="impressionUrl"
                                    label="Impression URL"
                                    variant="outlined"
                                    value={formData.impressionUrl || ''}
                                    onChange={handleChange}
                                />
                            </Row>
                            <AccessGuard accessAgency={`${AGENCY_CAMPAIGNS}`}>
                                <>
                                    {campaigns && (
                                        <Autocomplete
                                            sx={{ padding: '10px' }}
                                            options={campaigns}
                                            value={formData.campaign}
                                            getOptionLabel={(campaign) => campaign.campaignName || ''}
                                            onChange={handleCampaignChange}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    fullWidth
                                                    size="small"
                                                    label="Link Campaign (optional)"
                                                />
                                            )}
                                        />
                                    )}

                                    {formData?.campaign?.adGroups && (
                                        <Autocomplete
                                            sx={{ padding: '10px' }}
                                            options={formData?.campaign?.adGroups}
                                            value={formData.campaignAdGroup}
                                            getOptionLabel={(ag) => ag.name || ''}
                                            onChange={handleAdGroupChange}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    fullWidth
                                                    size="small"
                                                    label="Link Ad Group (optional)"
                                                />
                                            )}
                                        />
                                    )}
                                </>
                            </AccessGuard>
                            <Row
                                sx={{
                                    padding: '10px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <FormControlLabel
                                    control={
                                        <Switch
                                            name="emailNotification"
                                            checked={formData.emailNotification}
                                            onChange={handleSwitchChange}
                                            color="primary"
                                        />
                                    }
                                    label={
                                        <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                            Send tag via email
                                        </Typography>
                                    }
                                    labelPlacement="start"
                                    sx={{ width: '100%', justifyContent: 'space-between', margin: 0 }}
                                />
                            </Row>
                        </DialogContent>

                        <DialogActions sx={{ padding: '10px 20px' }}>
                            <CDButton color="error" onClick={handleDialogClose}>
                                Cancel
                            </CDButton>

                            <CDButton
                                type="submit"
                                disabled={
                                    isLoading ||
                                    (!formData.id &&
                                        !(formData.tagType === 'HTML_IMAGE'
                                            ? agencyImageSourceFolder
                                            : agencyVideoSourceFolder))
                                }
                            >
                                Generate Ad Tag
                            </CDButton>
                        </DialogActions>
                    </form>
                )}
            </Dialog>
        </>
    );
});

export default EditAdTagPlacement;
